import { useOpenCustomAppDialog } from "src/components/views/customApps/useOpenCustomAppDialog.tsx";
import { findIconDefinition, type IconName } from "@fortawesome/fontawesome-svg-core";
import i18n from "i18next";
import { useContext } from "react";
import { CustomFunctionUiActionApi } from "src/api/generated/erp/customFunctions/triggers/uiAction/api/customFunctionUiActionApi.ts";
import type { CustomFunctionUiActionContextItem } from "src/api/generated/erp/customFunctions/triggers/uiAction/context/customFunctionUiActionContextItem.ts";
import type { CustomFunctionUiActionExecutionResult } from "src/api/generated/erp/customFunctions/triggers/uiAction/customFunctionUiActionExecutionResult.ts";
import type { CustomFunctionUiActionView } from "src/api/generated/io/aavo/applications/db/erp/types/customFunctionUiActionView.ts";
import { AavoDataGridContext } from "src/components/common/dataGrid/AavoDataGridContext.tsx";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { AavoMenuDivider } from "src/components/common/menu/AavoMenuDivider.tsx";
import { useAavoToast } from "src/components/common/toast/AavoToastContext.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { useBackgroundOperations } from "src/components/views/backgroundOperations/backgroundOperationsContext.ts";
import { CustomFunctionUiActionInputDialog } from "src/components/views/erp/customFunctions/uiAction/CustomFunctionUiActionInputDialog.tsx";
import { openCustomFunctionUiActionMessageDialog } from "src/components/views/erp/customFunctions/uiAction/CustomFunctionUiActionMessageDialog.tsx";
import { handleCustomFunctionUiActionExecutionResult } from "src/components/views/erp/customFunctions/uiAction/customFunctionUiActionExecutionResult.ts";
import {
	selectCustomUiActions,
	selectCustomUiActionsIncludingZero,
} from "src/components/common/customUiActions/customUiActionSelection.ts";
import { useCustomUiActions } from "src/components/common/customUiActions/customUiActionsContext.ts";
import { logError } from "src/errorHandling/errorLogging.ts";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { getSelectedEnvironmentId } from "src/storage/appState/environment.ts";
import {
	clearCustomFunctionUiActionIdempotencyKey,
	getCustomFunctionUiActionIdempotencyKey,
} from "src/components/views/erp/customFunctions/uiAction/customFunctionUiActionIdempotency.ts";
import { CustomFunctionUiActionContinuation } from "src/components/views/erp/customFunctions/uiAction/CustomFunctionUiActionContinuation.tsx";
import { AavoMenuButton } from "src/components/common/menu/AavoMenuButton.tsx";

const UI_ACTION_DIALOG_CONTEXT_KEY = defineAgentUiContextKey("customFunctions.uiActionInputDialog");

interface CustomUiActionMenuItemsProps {
	view: CustomFunctionUiActionView;
	contextItems?: CustomFunctionUiActionContextItem[];
	includeZeroCardinalityActions?: boolean;
	refreshData?: () => Promise<unknown>;
}

interface CustomUiActionObjectMenuItemsProps {
	view: CustomFunctionUiActionView;
	objectIds?: number[];
	includeZeroCardinalityActions?: boolean;
	refreshData?: () => Promise<unknown>;
}

export function CustomUiActionObjectMenuButton({
	view,
	objectIds = [],
	includeZeroCardinalityActions = false,
	refreshData,
}: CustomUiActionObjectMenuItemsProps) {
	const allActions = useCustomUiActions();
	const contextItems = objectIds.map((objectId) => ({ type: "OBJECT" as const, objectId }));
	const actions =
		includeZeroCardinalityActions ?
			selectCustomUiActionsIncludingZero(allActions, view, contextItems)
		:	selectCustomUiActions(allActions, view, contextItems);
	if (actions.length === 0) return null;

	return (
		<AavoMenuButton
			menuContent={
				<CustomUiActionMenuItems
					view={view}
					contextItems={contextItems}
					includeZeroCardinalityActions={includeZeroCardinalityActions}
					refreshData={refreshData}
				/>
			}
		/>
	);
}

export function CustomUiActionObjectMenuItems({
	view,
	objectIds = [],
	refreshData,
}: CustomUiActionObjectMenuItemsProps) {
	return (
		<CustomUiActionMenuItems
			view={view}
			contextItems={objectIds.map((objectId) => ({ type: "OBJECT", objectId }))}
			refreshData={refreshData}
		/>
	);
}

export function CustomUiActionMenuItems({
	view,
	contextItems = [],
	includeZeroCardinalityActions = false,
	refreshData: explicitRefreshData,
}: CustomUiActionMenuItemsProps) {
	const openApp = useOpenCustomAppDialog();
	const { openDialog } = useGenericDialog();
	const { showToast } = useAavoToast();
	const { refreshActiveOperations } = useBackgroundOperations();
	const { principalId } = useGlobalInitData();
	const environmentId = getSelectedEnvironmentId();
	const dataGridRefreshData = useContext(AavoDataGridContext)?.refreshData;
	const refreshData = explicitRefreshData ?? dataGridRefreshData;
	const allActions = useCustomUiActions();
	const actionsWithContexts =
		includeZeroCardinalityActions ?
			selectCustomUiActionsIncludingZero(allActions, view, contextItems)
		:	selectCustomUiActions(allActions, view, contextItems);

	if (actionsWithContexts.length === 0) return null;

	async function handleExecutionResult(actionLabel: string, result: CustomFunctionUiActionExecutionResult) {
		if (result.inputRequest != null) {
			openDialog(({ closeDialog }) => ({
				title: actionLabel,
				size: "sm",
				agentContext: {
					key: UI_ACTION_DIALOG_CONTEXT_KEY,
					data: { executionId: result.executionId },
					pathSegments: [actionLabel],
				},
				content: (
					<CustomFunctionUiActionContinuation
						initialResult={result}
						closeDialog={closeDialog}
						onFinished={async (finishedResult) => {
							await closeDialog();
							await handleExecutionResult(actionLabel, finishedResult);
						}}
					/>
				),
			}));
			return;
		}
		await handleCustomFunctionUiActionExecutionResult(result, {
			downloadFile: (file) => downloadFile(file, { openInNewTab: false }),
			openMessage: (message) => openCustomFunctionUiActionMessageDialog(openDialog, actionLabel, message),
			refreshData,
			showBackgroundStarted: () =>
				showToast({
					title: i18n.t("background_task_started"),
					message: i18n.t("background_task_started_message"),
				}),
			refreshBackgroundOperations: () => refreshActiveOperations(true).catch(logError),
		});
	}

	return (
		<>
			{actionsWithContexts.map(({ action, executionContext }) => (
				<AsyncMenuButton
					key={
						action.kind === "app" ?
							`app-${action.customAppRevisionId}-${action.launcherKey}`
						:	`custom-function-${action.triggerLineageId}`
					}
					label={action.label}
					icon={findIconDefinition({ prefix: "far", iconName: action.iconName as IconName }) ?? null}
					onClick={async () => {
						if (action.kind === "app") {
							await openApp(action, executionContext, refreshData);
							return;
						}
						if (action.userInputSchema != null || action.fileInputs.length > 0) {
							openDialog(({ closeDialog, onContentEdited }) => ({
								title: action.label,
								size: "sm",
								agentContext: {
									key: UI_ACTION_DIALOG_CONTEXT_KEY,
									data: { triggerLineageId: action.triggerLineageId },
									pathSegments: [action.label],
								},
								content: (
									<CustomFunctionUiActionInputDialog
										principalId={principalId}
										environmentId={environmentId}
										triggerLineageId={action.triggerLineageId}
										context={executionContext}
										userInputSchema={action.userInputSchema}
										userInputPresentations={action.userInputPresentations ?? {}}
										fileInputs={action.fileInputs}
										closeDialog={closeDialog}
										onEdited={onContentEdited}
										onExecuted={(result) => handleExecutionResult(action.label, result)}
									/>
								),
							}));
							return;
						}
						const idempotencyKey = await getCustomFunctionUiActionIdempotencyKey(
							principalId,
							environmentId,
							action.triggerLineageId,
							executionContext,
						);
						const result = await CustomFunctionUiActionApi.executeUiAction({
							triggerLineageId: action.triggerLineageId,
							context: executionContext,
							idempotencyKey,
						});
						await clearCustomFunctionUiActionIdempotencyKey(
							principalId,
							environmentId,
							action.triggerLineageId,
							executionContext,
							idempotencyKey,
						);
						await handleExecutionResult(action.label, result);
					}}
				/>
			))}
			<AavoMenuDivider />
		</>
	);
}
